import { useRouter } from '@tanstack/react-router'
import { Check, VenetianMask } from 'lucide-react'

import { COMBINABLE_ROLES } from '@brickup/shared/constants'
import { USER_ROLES, UserRole } from '@brickup/shared/enums'
import { USER_ROLE_LABELS } from '@brickup/shared/locales/de'
import { PERMISSIONS } from '@brickup/shared/locales/de/permissions'
import { Badge, Button, Menu } from '@/components/base'
import { useAppStore } from '@/stores'

/**
 * What root may try on: each role alone, and the roles that combine held
 * together — an architect who also sells sees what neither alone does.
 */
const ROLE_SETS: readonly (readonly UserRole[])[] = [
  ...USER_ROLES.map(role => [role]),
  ...(COMBINABLE_ROLES.length > 1 ? [COMBINABLE_ROLES] : []),
]

const setKey = (roles: readonly UserRole[]) => roles.join('+')
const setLabel = (roles: readonly UserRole[]) =>
  roles.map(role => USER_ROLE_LABELS[role]).join(' + ')

/**
 * Looking at the app as another role.
 *
 * Only the frontend: what the reader is signed in as does not change, and the
 * server answers every request as root either way. It is here to answer "what
 * does an advisor actually see", which is otherwise a second account and a
 * second browser.
 *
 * Offered to root alone, and read from `realRoles` rather than the effective
 * one — assuming a role must not take away the way back out of it.
 *
 * @single-use the one control that changes who the app thinks is reading
 */
export function AssumeRoleMenu() {
  const router = useRouter()
  const realRoles = useAppStore(state => state.realRoles)
  const assumedRoles = useAppStore(state => state.assumedRoles)
  const assumeRoles = useAppStore(state => state.assumeRoles)

  if (!realRoles?.includes(UserRole.ROOT)) return null

  const assumedKey = assumedRoles ? setKey(assumedRoles) : null

  const assume = (roles: readonly UserRole[] | null) => {
    assumeRoles(roles)
    // The guard already ran for the page in front of them; without this it
    // would keep an area the new role may not open on screen until they
    // navigate.
    void router.invalidate()
  }

  return (
    <Menu
      reads
      label={PERMISSIONS.assumeRole.label}
      actions={[
        ...ROLE_SETS.map(roles => ({
          key: setKey(roles),
          label: setLabel(roles),
          icon: setKey(roles) === assumedKey ? Check : undefined,
          onSelect: () => assume(roles),
        })),
        {
          key: 'reset',
          label: PERMISSIONS.assumeRole.reset,
          icon: assumedRoles === null ? Check : undefined,
          onSelect: () => assume(null),
        },
      ]}
      trigger={
        assumedRoles ? (
          <Button
            reads
            size="sm"
            variant="outline"
            icon={VenetianMask}
            tooltip={PERMISSIONS.assumeRole.hint}
          >
            <Badge
              size="sm"
              variant="warning"
              label={PERMISSIONS.assumeRole.active(setLabel(assumedRoles))}
            />
          </Button>
        ) : (
          <Button
            reads
            size="icon"
            variant="ghost"
            icon={VenetianMask}
            tooltip={PERMISSIONS.assumeRole.label}
          />
        )
      }
    />
  )
}
